import { Controller } from "@hotwired/stimulus"

// Controller for real-time seller code validation
export default class extends Controller {
  static targets = ["input", "result", "loading"]
  static values = {
    url: String,
    minLength: { type: Number, default: 4 }
  }

  connect() {
    this.timeout = null
    // Auto-validate on page load if there's already a value (e.g., after form re-render)
    if (this.hasInputTarget && this.inputTarget.value.trim().length >= this.minLengthValue) {
      this.validate()
    }
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
  }

  validate() {
    // Auto-uppercase the input value as user types
    const cursorPosition = this.inputTarget.selectionStart
    const uppercaseValue = this.inputTarget.value.toUpperCase()
    if (this.inputTarget.value !== uppercaseValue) {
      this.inputTarget.value = uppercaseValue
      // Restore cursor position after value change
      this.inputTarget.setSelectionRange(cursorPosition, cursorPosition)
    }

    const code = uppercaseValue.trim()

    // Clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    // Hide result if code is too short
    if (code.length < this.minLengthValue) {
      this.hideResult()
      this.hideLoading()
      return
    }

    // Show loading indicator
    this.showLoading()

    // Debounce the API call
    this.timeout = setTimeout(() => {
      this.fetchSellerInfo(code)
    }, 300)
  }

  async fetchSellerInfo(code) {
    try {
      const response = await fetch(`${this.urlValue}?code=${encodeURIComponent(code)}`)
      const data = await response.json()

      this.hideLoading()

      // Handle rate limiting
      if (response.status === 429 || data.rate_limited) {
        this.showWarning(data.message || "Aguarde um momento...")
        return
      }

      if (data.valid) {
        this.showSuccess(data.message)
      } else if (data.message) {
        this.showError(data.message)
      } else {
        this.hideResult()
      }
    } catch (error) {
      this.hideLoading()
      this.hideResult()
      console.error("Error validating seller code:", error)
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    if (this.hasResultTarget) {
      this.resultTarget.classList.add("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  showSuccess(message) {
    if (this.hasResultTarget) {
      this.resultTarget.textContent = message
      this.resultTarget.classList.remove("hidden", "text-red-600", "text-yellow-600")
      this.resultTarget.classList.add("text-green-600")
    }
    // Update input border
    this.inputTarget.classList.remove("border-red-300", "border-gray-300", "border-yellow-400")
    this.inputTarget.classList.add("border-green-500")
  }

  showError(message) {
    if (this.hasResultTarget) {
      this.resultTarget.textContent = message
      this.resultTarget.classList.remove("hidden", "text-green-600", "text-yellow-600")
      this.resultTarget.classList.add("text-red-600")
    }
    // Update input border
    this.inputTarget.classList.remove("border-green-500", "border-gray-300", "border-yellow-400")
    this.inputTarget.classList.add("border-red-300")
  }

  showWarning(message) {
    if (this.hasResultTarget) {
      this.resultTarget.textContent = message
      this.resultTarget.classList.remove("hidden", "text-green-600", "text-red-600")
      this.resultTarget.classList.add("text-yellow-600")
    }
    // Update input border
    this.inputTarget.classList.remove("border-green-500", "border-gray-300", "border-red-300")
    this.inputTarget.classList.add("border-yellow-400")
  }

  hideResult() {
    if (this.hasResultTarget) {
      this.resultTarget.classList.add("hidden")
    }
    // Reset input border
    this.inputTarget.classList.remove("border-green-500", "border-red-300", "border-yellow-400")
    this.inputTarget.classList.add("border-gray-300")
  }
}
